/**
 * Componente de Alertas de Saúde da Fila de Webhooks PGMQ
 * 
 * Exibe alertas automáticos baseados nas métricas da fila:
 * - Pendentes altos
 * - Itens stuck em processamento
 * - Itens na DLQ
 * - Taxa de sucesso baixa
 * - Latência alta
 */

import { AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useWebhookQueueAlerts, WebhookQueueType } from '@/hooks/useWebhookQueueMonitor';

interface WebhookQueueHealthAlertsProps {
  queue: WebhookQueueType;
}

export function WebhookQueueHealthAlerts({ queue }: WebhookQueueHealthAlertsProps) {
  const { data: alerts, isLoading } = useWebhookQueueAlerts(queue);

  if (isLoading) {
    return null;
  }

  // Se não houver alertas, exibir mensagem de sucesso
  if (!alerts || alerts.length === 0) {
    return (
      <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-800 dark:text-green-200">
          Fila Saudável
        </AlertTitle>
        <AlertDescription className="text-green-700 dark:text-green-300">
          Nenhum problema detectado na fila de {queue}.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          Alertas de Saúde ({alerts.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {alerts.map((alert) => (
          <Alert 
            key={alert.id}
            variant={alert.type === 'error' ? 'destructive' : 'default'}
            className={alert.type === 'warning' 
              ? 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20' 
              : ''
            }
          >
            {alert.type === 'error' ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            )}
            <AlertDescription className={alert.type === 'warning' 
              ? 'text-yellow-800 dark:text-yellow-200' 
              : ''
            }>
              {alert.message}
            </AlertDescription>
          </Alert>
        ))}
      </CardContent>
    </Card>
  );
}
